import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Avatar,
    Chip,
    Divider,
    Button,
    IconButton,
    CircularProgress,
    Stack,
    Select,
    MenuItem,
    CardContent,
    Fade,
    Breadcrumbs,
    Link,
    Tooltip,
    alpha,
    LinearProgress,
} from '@mui/material';
import {
    ArrowBack,
    Person,
    Work,
    AccountBalance,
    Description,
    Email,
    Phone,
    LocationOn,
    CalendarToday,
    SupervisorAccount,
    Download,
    Verified,
    Info,
    AccountTree,
    Print,
    Warning,
    NavigateNext,
} from '@mui/icons-material';
import apiClient from '../../../../api/apiClient';
import { getEmployeeById, updateDocumentStatus, updateBankDetailsStatus, updateIdentityStatus } from '../../../../api/employeeApi';
import { StyledCard, CardHeader } from './styles/components';
import { saveAs } from 'file-saver';

const PendingDocumentsReport = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [verifyingDocId, setVerifyingDocId] = useState(null);
    const [verifyingIdentity, setVerifyingIdentity] = useState(null);
    const [verifyingBank, setVerifyingBank] = useState(false);

    const fetchEmployeeDetails = async () => {
        try {
            setLoading(true);
            const response = await getEmployeeById(id);
            if (response.success) {
                setEmployee(response.data);
            }
        } catch (error) {
            console.error('Error fetching employee details:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployeeDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleDownloadSingleFile = async (fileUrl, documentName = 'document', documentType = 'Document') => {
        if (!fileUrl) return;
        try {
            const proxyUrl = `/onboarding/proxy-download?url=${encodeURIComponent(fileUrl)}`;
            const response = await apiClient.get(proxyUrl, {
                responseType: 'blob',
                skipGlobalError: true,
                skipLoading: true
            });
            const blob = response.data;
            let extension = fileUrl.split('.').pop().split('?')[0];
            if (!extension || extension.length > 5) extension = 'pdf';
            let fileName = `${documentType.replace(/[/\\?%*:|"<>]/g, '-')}_${documentName}`.replace(/ /g, '_');
            if (!fileName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
                fileName = `${fileName}.${extension}`;
            }
            saveAs(blob, fileName);
        } catch (error) {
            console.error('Error downloading document:', error);
            window.open(fileUrl, '_blank');
        }
    };

    const handleVerifyIdentity = async (docType, newStatus) => {
        try {
            setVerifyingIdentity(docType);
            const result = await updateIdentityStatus(employee.id, docType, newStatus);
            if (result.success) {
                fetchEmployeeDetails();
            }
        } catch (error) {
            console.error('Error updating identity status:', error);
        } finally {
            setVerifyingIdentity(null);
        }
    };

    const handleVerifyBankDetails = async (newStatus) => {
        try {
            setVerifyingBank(true);
            const result = await updateBankDetailsStatus(employee.id, newStatus);
            if (result.success) {
                fetchEmployeeDetails();
            }
        } catch (error) {
            console.error('Error updating bank status:', error);
        } finally {
            setVerifyingBank(false);
        }
    };

    const handleVerifyDocument = async (docId, newStatus) => {
        try {
            setVerifyingDocId(docId);
            const result = await updateDocumentStatus(docId, newStatus);
            if (result.success) {
                fetchEmployeeDetails();
            }
        } catch (error) {
            console.error('Error updating document status:', error);
        } finally {
            setVerifyingDocId(null);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadCSV = () => {
        if (!employee) return;
        const headers = ['Category', 'Document Name', 'Status'];
        const rows = checklist.map(item => [
            item.category,
            item.label,
            item.status === 'Uploaded' || item.status === 'Awaiting Verification' ? 'Awaiting Review' : item.status
        ]);

        const csvContent = [
            ['Pending Documents Audit Report'],
            [`Employee: ${employee.firstName} ${employee.lastName} (${employee.employeeId || 'N/A'})`],
            [`Department: ${employee.department?.name || 'N/A'}`],
            [`Generated on: ${new Date().toLocaleDateString('en-GB')}`],
            [],
            headers,
            ...rows
        ]
        .map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
        .join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `Pending_Documents_${employee.firstName}_${employee.lastName}.csv`);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress sx={{ color: '#7522D1' }} />
            </Box>
        );
    }

    if (!employee) {
        return (
            <Container sx={{ py: 5 }}>
                <Typography color="error">Employee not found.</Typography>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/my-team/employee-directory')}>Back</Button>
            </Container>
        );
    }

    // Compile Document Checklist dynamically
    const checklist = [];
    const identity = employee.identities?.[0] || {};
    const bank = employee.bankDetails || {};
    const docs = employee.documents || [];
    const comp = employee.compensation || {};

    const isPFEligible = comp.hasExistingPF === true || comp.hasExistingPF === 'yes' || comp.hasExistingPF === 'true';
    const isESICEligible = comp.monthlyGross !== undefined && comp.monthlyGross !== null && parseFloat(comp.monthlyGross) < 21000;

    // 1. Identity & Legal
    checklist.push({
        id: 'aadhar_front',
        category: 'Identity & Legal',
        label: 'Aadhaar Card (Front)',
        uploaded: !!identity.aadhaarFrontFilePath,
        fileUrl: identity.aadhaarFrontFilePath,
        status: identity.aadhaarFrontFilePath ? (identity.aadhaarStatus || 'Pending') : 'Pending Upload',
        verifyAction: (status) => handleVerifyIdentity('aadhar', status),
        verifying: verifyingIdentity === 'aadhar',
        isIdentity: true
    });

    checklist.push({
        id: 'aadhar_back',
        category: 'Identity & Legal',
        label: 'Aadhaar Card (Back)',
        uploaded: !!identity.aadhaarBackFilePath,
        fileUrl: identity.aadhaarBackFilePath,
        status: identity.aadhaarBackFilePath ? (identity.aadhaarStatus || 'Pending') : 'Pending Upload',
        verifyAction: (status) => handleVerifyIdentity('aadhar', status),
        verifying: verifyingIdentity === 'aadhar',
        isIdentity: true
    });

    checklist.push({
        id: 'pan_card',
        category: 'Identity & Legal',
        label: 'PAN Card',
        uploaded: !!identity.panFilePath,
        fileUrl: identity.panFilePath,
        status: identity.panFilePath ? (identity.panStatus || 'Pending') : 'Pending Upload',
        verifyAction: (status) => handleVerifyIdentity('pan', status),
        verifying: verifyingIdentity === 'pan',
        isIdentity: true
    });

    // 2. Bank Verification
    const hasBankProof = docs.some(d => d.documentType === 'Bank Passbook/Cancelled Check') || !!bank.passbookDocumentPath;
    const bankProofDoc = docs.find(d => d.documentType === 'Bank Passbook/Cancelled Check');
    checklist.push({
        id: 'bank_proof',
        category: 'Banking',
        label: 'Bank Passbook / Cancelled Cheque',
        uploaded: hasBankProof,
        fileUrl: bankProofDoc?.fileUrl || bank.passbookDocumentPath,
        status: hasBankProof ? (bank.status || 'Pending') : 'Pending Upload',
        verifyAction: (status) => handleVerifyBankDetails(status),
        verifying: verifyingBank
    });

    // 3. Education Checklist
    const hasEduRecords = employee.education && employee.education.length > 0;

    // Class 10th
    const edu10 = employee.education?.find(e => e.qualificationLevel?.includes('10') || e.degree?.includes('10'));
    const expects10th = !hasEduRecords || !!edu10;
    if (expects10th) {
        const doc10 = edu10?.documentPath 
            ? docs.find(d => d.fileUrl === edu10.documentPath) 
            : docs.find(d => d.documentType === 'Education Document' && d.documentName?.toLowerCase().includes('10'));
        
        checklist.push({
            id: doc10?.id || 'edu_10',
            category: 'Education',
            label: 'Class 10th Certificate',
            uploaded: !!edu10?.documentPath || !!doc10?.fileUrl,
            fileUrl: doc10?.fileUrl || edu10?.documentPath,
            status: (doc10?.fileUrl || edu10?.documentPath) ? (doc10?.status || 'Uploaded') : 'Pending Upload',
            verifyAction: doc10?.id ? (status) => handleVerifyDocument(doc10.id, status) : null,
            verifying: doc10?.id ? verifyingDocId === doc10.id : false
        });
    }

    // Class 12th / Diploma
    const edu12 = employee.education?.find(e => e.qualificationLevel?.includes('12') || e.degree?.includes('12') || e.qualificationLevel?.toLowerCase().includes('diploma'));
    const expects12th = !hasEduRecords || !!edu12;
    if (expects12th) {
        const doc12 = edu12?.documentPath 
            ? docs.find(d => d.fileUrl === edu12.documentPath) 
            : docs.find(d => d.documentType === 'Education Document' && (d.documentName?.toLowerCase().includes('12') || d.documentName?.toLowerCase().includes('diploma')));
        
        checklist.push({
            id: doc12?.id || 'edu_12',
            category: 'Education',
            label: 'Class 12th / Diploma Certificate',
            uploaded: !!edu12?.documentPath || !!doc12?.fileUrl,
            fileUrl: doc12?.fileUrl || edu12?.documentPath,
            status: (doc12?.fileUrl || edu12?.documentPath) ? (doc12?.status || 'Uploaded') : 'Pending Upload',
            verifyAction: doc12?.id ? (status) => handleVerifyDocument(doc12.id, status) : null,
            verifying: doc12?.id ? verifyingDocId === doc12.id : false
        });
    }

    // Graduation
    const eduGrad = employee.education?.find(e => e.qualificationLevel?.toLowerCase().includes('graduation') || e.qualificationLevel?.toLowerCase().includes('degree') || (!e.qualificationLevel?.includes('10') && !e.qualificationLevel?.includes('12')));
    const expectsGrad = !hasEduRecords || !!eduGrad;
    if (expectsGrad) {
        const docGrad = eduGrad?.documentPath 
            ? docs.find(d => d.fileUrl === eduGrad.documentPath) 
            : docs.find(d => d.documentType === 'Education Document' && !d.documentName?.toLowerCase().includes('10') && !d.documentName?.toLowerCase().includes('12'));
        
        checklist.push({
            id: docGrad?.id || 'edu_grad',
            category: 'Education',
            label: 'Graduation / Degree Certificate',
            uploaded: !!eduGrad?.documentPath || !!docGrad?.fileUrl,
            fileUrl: docGrad?.fileUrl || eduGrad?.documentPath,
            status: (docGrad?.fileUrl || eduGrad?.documentPath) ? (docGrad?.status || 'Uploaded') : 'Pending Upload',
            verifyAction: docGrad?.id ? (status) => handleVerifyDocument(docGrad.id, status) : null,
            verifying: docGrad?.id ? verifyingDocId === docGrad.id : false
        });
    }

    // 4. Conditional disability certificate
    if (employee.personalInfo?.isPhysicallyChallenged) {
        const disCert = docs.find(d => d.documentType === 'Disability Certificate') || employee.personalInfo?.physicallyChallengedCertificatePath;
        checklist.push({
            id: disCert?.id || 'disability_cert',
            category: 'Identity & Legal',
            label: 'Disability Certificate',
            uploaded: !!disCert,
            fileUrl: disCert?.fileUrl || employee.personalInfo?.physicallyChallengedCertificatePath,
            status: disCert ? (disCert.status || 'Verified') : 'Pending Upload',
            verifyAction: disCert?.id ? (status) => handleVerifyDocument(disCert.id, status) : null,
            verifying: disCert?.id ? verifyingDocId === disCert.id : false
        });
    }

    // 5. Joining Forms
    // Dynamic Templates assigned by HR during Initiate Onboarding
    const requirements = employee.onboardingRequirements || [];
    if (requirements.length > 0) {
        requirements.forEach((req, idx) => {
            if (req.template || req.templateName || req.label) {
                const templateName = req.template?.label || req.template?.templateName || req.label || req.templateName;
                const formDoc = docs.find(d => 
                    d.documentType === `Joining Form: ${templateName}` || 
                    d.documentType === templateName || 
                    (d.documentType || '').toLowerCase().includes(templateName.toLowerCase())
                );

                checklist.push({
                    id: formDoc?.id || `req_form_${idx}`,
                    category: 'Joining & Onboarding Forms',
                    label: templateName,
                    uploaded: !!formDoc?.fileUrl,
                    fileUrl: formDoc?.fileUrl,
                    status: formDoc?.fileUrl ? (formDoc.status || 'Uploaded') : 'Pending Upload',
                    verifyAction: formDoc?.id ? (status) => handleVerifyDocument(formDoc.id, status) : null,
                    verifying: formDoc?.id ? verifyingDocId === formDoc.id : false
                });
            }
        });
    }

    // Toggled Joining Forms based on HR selection during Initiate Onboarding
    const requiresPFDeclaration = comp.hasExistingPF === true || comp.hasExistingPF === 'true' || comp.hasExistingPF === 'yes';
    if (requiresPFDeclaration) {
        const formDoc = docs.find(d => {
            const docTypeLower = (d.documentType || '').toLowerCase();
            return docTypeLower.includes('non-pf') || docTypeLower.includes('non pf') || docTypeLower.includes('form 11') || docTypeLower.includes('form11');
        });
        checklist.push({
            id: formDoc?.id || 'joining_form_non_pf',
            category: 'Joining & Onboarding Forms',
            label: 'Declaration of Non-PF',
            uploaded: !!formDoc?.fileUrl,
            fileUrl: formDoc?.fileUrl,
            status: formDoc?.fileUrl ? (formDoc.status || 'Uploaded') : 'Pending Upload',
            verifyAction: formDoc?.id ? (status) => handleVerifyDocument(formDoc.id, status) : null,
            verifying: formDoc?.id ? verifyingDocId === formDoc.id : false
        });
    }

    const requires12BB = comp.collect12BB === true || comp.collect12BB === 'true' || comp.collect12BB === 'yes';
    if (requires12BB) {
        const formDoc = docs.find(d => (d.documentType || '').toLowerCase().includes('12bb'));
        checklist.push({
            id: formDoc?.id || 'joining_form_12bb',
            category: 'Joining & Onboarding Forms',
            label: 'Form 12BB',
            uploaded: !!formDoc?.fileUrl,
            fileUrl: formDoc?.fileUrl,
            status: formDoc?.fileUrl ? (formDoc.status || 'Uploaded') : 'Pending Upload',
            verifyAction: formDoc?.id ? (status) => handleVerifyDocument(formDoc.id, status) : null,
            verifying: formDoc?.id ? verifyingDocId === formDoc.id : false
        });
    }

    const requiresESIC = comp.collectESIC === true || comp.collectESIC === 'true' || comp.collectESIC === 'yes';
    if (requiresESIC) {
        const formDoc = docs.find(d => (d.documentType || '').toLowerCase().includes('esic'));
        checklist.push({
            id: formDoc?.id || 'joining_form_esic',
            category: 'Joining & Onboarding Forms',
            label: 'ESIC Form 1',
            uploaded: !!formDoc?.fileUrl,
            fileUrl: formDoc?.fileUrl,
            status: formDoc?.fileUrl ? (formDoc.status || 'Uploaded') : 'Pending Upload',
            verifyAction: formDoc?.id ? (status) => handleVerifyDocument(formDoc.id, status) : null,
            verifying: formDoc?.id ? verifyingDocId === formDoc.id : false
        });
    }

    // Passport Size Photo
    const photoDoc = docs.find(d => (d.documentType || '').toLowerCase().includes('photo')) || (employee.personalInfo?.passportPhotoPath ? { fileUrl: employee.personalInfo.passportPhotoPath, status: 'Uploaded' } : null);
    checklist.push({
        id: photoDoc?.id || 'joining_form_photo',
        category: 'Joining & Onboarding Forms',
        label: 'Passport Size Photograph',
        uploaded: !!photoDoc?.fileUrl,
        fileUrl: photoDoc?.fileUrl,
        status: photoDoc?.fileUrl ? (photoDoc.status || 'Uploaded') : 'Pending Upload',
        verifyAction: photoDoc?.id ? (status) => handleVerifyDocument(photoDoc.id, status) : null,
        verifying: photoDoc?.id ? verifyingDocId === photoDoc.id : false
    });

    const totalDocs = checklist.length;
    const uploadedDocs = checklist.filter(c => c.uploaded).length;
    const verifiedDocs = checklist.filter(c => c.status === 'Verified').length;
    const completionPercent = totalDocs > 0 ? Math.round((uploadedDocs / totalDocs) * 100) : 0;

    const getStatusChip = (status) => {
        switch (status) {
            case 'Verified':
                return <Chip label="Verified" color="success" size="small" sx={{ fontWeight: 700, borderRadius: '6px' }} />;
            case 'Pending':
            case 'Uploaded':
            case 'Awaiting Verification':
                return <Chip label="Awaiting Review" color="warning" size="small" sx={{ fontWeight: 700, borderRadius: '6px' }} />;
            case 'Pending Upload':
            default:
                return <Chip label="Pending Upload" color="error" size="small" sx={{ fontWeight: 700, borderRadius: '6px' }} />;
        }
    };

    return (
        <Fade in={true} timeout={600}>
            <Container maxWidth="xl" sx={{ py: 4, '@media print': { py: 0 } }}>
                {/* Print layout styles */}
                <style dangerouslySetInnerHTML={{ __html: `
                    @media print {
                        body {
                            background: white !important;
                            color: black !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                        .print-shadow {
                            box-shadow: none !important;
                            border: 1px solid #cbd5e1 !important;
                        }
                    }
                `}} />

                {/* Breadcrumbs and Actions */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }} className="no-print">
                    <Breadcrumbs
                        separator={<NavigateNext fontSize="small" sx={{ color: "#94a3b8" }} />}
                        sx={{
                            "& .MuiBreadcrumbs-li": {
                                fontSize: "0.875rem",
                            },
                        }}
                    >
                        <Link
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate("/dashboard")}
                            sx={{
                                cursor: "pointer",
                                color: "#4F4937",
                                fontWeight: 400,
                                fontSize: "0.875rem",
                                "&:hover": { color: "#7522D1" },
                            }}
                        >
                            Dashboard
                        </Link>
                        <Link
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate("/my-team")}
                            sx={{
                                cursor: "pointer",
                                color: "#4F4937",
                                fontWeight: 400,
                                fontSize: "0.875rem",
                                "&:hover": { color: "#7522D1" },
                            }}
                        >
                            My Team
                        </Link>
                        <Link
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate("/my-team/pending-documents")}
                            sx={{
                                cursor: "pointer",
                                color: "#4F4937",
                                fontWeight: 400,
                                fontSize: "0.875rem",
                                "&:hover": { color: "#7522D1" },
                            }}
                        >
                            Pending Documents
                        </Link>
                        <Typography
                            color="text.primary"
                            sx={{
                                color: "#7522D1",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                            }}
                        >
                            {employee.firstName} {employee.lastName} Audit
                        </Typography>
                    </Breadcrumbs>

                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(`/my-team/employee-directory/${employee.id}`)}
                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Back to Profile
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Print />}
                            onClick={handlePrint}
                            sx={{ bgcolor: '#7522D1', '&:hover': { bgcolor: '#5E1BA8' }, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Print Report
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={handleDownloadCSV}
                            sx={{ bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' }, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Download Report
                        </Button>
                    </Stack>
                </Stack>

                {/* Print Title (Only visible in Print) */}
                <Box sx={{ display: 'none', '@media print': { display: 'block', mb: 3 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>Pending Documents Audit Report</Typography>
                    <Typography variant="body2" color="text.secondary">Generated on: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</Typography>
                    <Divider sx={{ my: 2 }} />
                </Box>

                {/* Profile Overview Card */}
                <StyledCard sx={{ mb: 4 }} className="print-shadow">
                    <CardContent sx={{ p: 4 }}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" spacing={3} alignItems="center">
                                    <Avatar
                                        src={employee.profileImage}
                                        sx={{ width: 90, height: 90, bgcolor: alpha('#7522D1', 0.1), color: '#7522D1', fontSize: 32, fontWeight: 700 }}
                                    >
                                        {employee.firstName?.[0]}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E293B' }}>
                                            {employee.firstName} {employee.lastName}
                                        </Typography>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                                            Emp ID: {employee.employeeId || 'N/A'}
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                            <Chip icon={<Work fontSize="small" />} label={employee.designation?.name || 'N/A'} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                                            <Chip icon={<AccountTree fontSize="small" />} label={employee.department?.name || 'N/A'} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                                            <Chip icon={<LocationOn fontSize="small" />} label={employee.jobLocation || 'N/A'} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ bgcolor: alpha('#7522D1', 0.02), border: '1px dashed', borderColor: alpha('#7522D1', 0.2), borderRadius: 3, p: 3 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>Document Completion Status</Typography>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#7522D1' }}>{uploadedDocs} / {totalDocs} Uploaded</Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={completionPercent}
                                        sx={{
                                            height: 10,
                                            borderRadius: 5,
                                            bgcolor: alpha('#7522D1', 0.1),
                                            '& .MuiLinearProgress-bar': { bgcolor: '#7522D1', borderRadius: 5 }
                                        }}
                                    />
                                    <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700 }}>VERIFIED</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#10B981' }}>{verifiedDocs}</Typography>
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700 }}>AWAITING REVIEW</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#F59E0B' }}>{uploadedDocs - verifiedDocs}</Typography>
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700 }}>PENDING UPLOAD</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#EF4444' }}>{totalDocs - uploadedDocs}</Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </StyledCard>

                {/* Primary Content Split */}
                <Grid container spacing={4}>
                    {/* Left Panel: Employee Details Summary */}
                    <Grid item xs={12} lg={4}>
                        <Stack spacing={3}>
                            <StyledCard className="print-shadow">
                                <CardHeader title="Employee Info" icon={<Person sx={{ color: '#7522D1' }} />} />
                                <CardContent sx={{ p: 3 }}>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Personal Email</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <Email sx={{ fontSize: 18, color: '#64748B' }} /> {employee.personalInfo?.personalEmail || employee.email || 'N/A'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Alternative Contact</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <Phone sx={{ fontSize: 18, color: '#64748B' }} /> {employee.personalInfo?.alternativePhone || employee.phone || 'N/A'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Date of Joining</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <CalendarToday sx={{ fontSize: 18, color: '#64748B' }} /> {employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Reporting Manager</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <SupervisorAccount sx={{ fontSize: 18, color: '#64748B' }} /> {employee.manager ? `${employee.manager.firstName} ${employee.manager.lastName}` : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </StyledCard>

                            <StyledCard className="print-shadow">
                                <CardHeader title="Eligibility & Setup" icon={<AccountBalance sx={{ color: '#10B981' }} />} />
                                <CardContent sx={{ p: 3 }}>
                                    <Stack spacing={2}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748B' }}>PF Eligible</Typography>
                                            <Chip label={isPFEligible ? 'Eligible' : 'Not Eligible'} color={isPFEligible ? 'success' : 'default'} size="small" sx={{ fontWeight: 700 }} />
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748B' }}>ESIC Eligible</Typography>
                                            <Chip label={isESICEligible ? 'Eligible' : 'Not Eligible'} color={isESICEligible ? 'success' : 'default'} size="small" sx={{ fontWeight: 700 }} />
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </StyledCard>
                        </Stack>
                    </Grid>

                    {/* Right Panel: Pending Document Checklist */}
                    <Grid item xs={12} lg={8}>
                        <StyledCard className="print-shadow">
                            <CardHeader
                                title="Onboarding Documents Checklist"
                                icon={<Description sx={{ color: '#7522D1' }} />}
                                action={
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B' }}>
                                        Audit status of all required documents
                                    </Typography>
                                }
                            />
                            <CardContent sx={{ p: 0 }}>
                                {['Identity & Legal', 'Banking', 'Education', 'Joining & Onboarding Forms'].map((category, catIdx) => {
                                    const items = checklist.filter(c => c.category === category);
                                    if (items.length === 0) return null;

                                    return (
                                        <Box key={category} sx={{ borderBottom: catIdx < 3 ? '1px solid #F1F5F9' : 'none', pb: 2 }}>
                                            <Box sx={{ bgcolor: '#F8FAFC', px: 3, py: 1.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {category}
                                                </Typography>
                                            </Box>
                                            <Stack spacing={0.5} sx={{ p: 2 }}>
                                                {items.map((item, itemIdx) => (
                                                    <Paper
                                                        key={item.id}
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 3,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 2,
                                                            transition: '0.2s',
                                                            borderColor: item.status === 'Verified' ? '#E2E8F0' : alpha('#7522D1', 0.15),
                                                            bgcolor: item.status === 'Verified' ? 'white' : alpha('#7522D1', 0.01),
                                                            '&:hover': {
                                                                borderColor: '#7522D1',
                                                                bgcolor: '#F8FAFC'
                                                            }
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                                                            {item.status === 'Verified' ? (
                                                                <Avatar sx={{ bgcolor: alpha('#10B981', 0.1), color: '#10B981' }}><Verified /></Avatar>
                                                            ) : item.uploaded ? (
                                                                <Avatar sx={{ bgcolor: alpha('#F59E0B', 0.1), color: '#F59E0B' }}><Info /></Avatar>
                                                            ) : (
                                                                <Avatar sx={{ bgcolor: alpha('#EF4444', 0.1), color: '#EF4444' }}><Warning /></Avatar>
                                                            )}
                                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: '#1E293B' }}>{item.label}</Typography>
                                                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                                                                    {getStatusChip(item.status)}
                                                                </Stack>
                                                            </Box>
                                                        </Stack>

                                                        <Stack direction="row" spacing={1.5} alignItems="center" className="no-print">
                                                            {item.uploaded && item.fileUrl && (
                                                                <Tooltip title="Download Document">
                                                                    <IconButton
                                                                        onClick={() => handleDownloadSingleFile(item.fileUrl, item.label.toLowerCase().replace(/ /g, '_'), item.category)}
                                                                        size="small"
                                                                        sx={{ bgcolor: alpha('#7522D1', 0.05), color: '#7522D1', '&:hover': { bgcolor: alpha('#7522D1', 0.1) } }}
                                                                    >
                                                                        <Download fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}

                                                            {item.uploaded && item.status !== 'Verified' && item.verifyAction && (
                                                                <Select
                                                                    size="small"
                                                                    value=""
                                                                    displayEmpty
                                                                    onChange={(e) => item.verifyAction(e.target.value)}
                                                                    disabled={item.verifying}
                                                                    sx={{
                                                                        borderRadius: '6px',
                                                                        height: 32,
                                                                        fontWeight: 700,
                                                                        minWidth: 100,
                                                                        bgcolor: '#10B981',
                                                                        color: 'white',
                                                                        '& .MuiSelect-icon': { color: 'white' },
                                                                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                                                        '& .MuiSelect-select': { py: 0.5, px: 1.5 }
                                                                    }}
                                                                    renderValue={() => item.verifying ? <CircularProgress size={14} color="inherit" /> : "Action"}
                                                                >
                                                                    <MenuItem value="Verified" sx={{ color: 'success.main', fontWeight: 700 }}>Verify</MenuItem>
                                                                    <MenuItem value="Rejected" sx={{ color: 'error.main', fontWeight: 700 }}>Reject</MenuItem>
                                                                </Select>
                                                            )}

                                                            {!item.uploaded && (
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#EF4444' }}>
                                                                    Awaiting upload from employee
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Paper>
                                                ))}
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </CardContent>
                        </StyledCard>
                    </Grid>
                </Grid>
            </Container>
        </Fade>
    );
};

export default PendingDocumentsReport;
